import { useId } from 'react'
import Link from 'next/link'
import { ArrowRight, MessageCircleQuestion, Plus } from 'lucide-react'
import './faq-v2.css'

// Every question AND answer is in the server HTML. Each item is a native
// <details>, so the answer stays in the DOM when collapsed (the browser hides
// it, it is never removed) and the accordion still works with JavaScript off.
// The old version only rendered the open panel, which left crawlers with one
// answer out of sixteen.
//
// `name` makes the items an exclusive group (opening one closes the others),
// matching the previous one-open-at-a-time behaviour.
/**
 * @param {{ eyebrow?: string, title?: string, titleAs?: string, intro?: string,
 *   faqs?: { q: string, a: string }[], className?: string }} props
 */
export default function FaqSection({
  eyebrow = 'FAQ',
  title = 'Frequently Asked Questions',
  // Every other page that embeds this section already has its own h1 in the
  // hero above, so this heading is correctly an h2 there. The standalone
  // /faq page has nothing else on it, so it passes 'h1' to make this the
  // page's one and only h1 instead of leaving the page without one.
  titleAs = 'h2',
  intro,
  faqs = [],
  className = '',
}) {
  const uid = useId()
  const Title = titleAs

  if (!faqs.length) return null

  return (
    <section className={`section faq2 ${className}`.trim()}>
      <div className="container faq2-grid">
        {/* Sticky rail. The heading and the escape hatch stay with the reader
            as they work down a long list, instead of scrolling away at the
            top of a two-column block. */}
        <aside className="faq2-rail">
          <span className="faq2-eyebrow">{eyebrow}</span>
          <Title>{title}</Title>
          {intro ? <p className="faq2-intro">{intro}</p> : null}

          <div className="faq2-help">
            <span className="ic ic-solid faq2-help-icon" aria-hidden="true"><MessageCircleQuestion size={20} /></span>
            <strong>Still have a question?</strong>
            <p>Ask us directly, same-day reply, no commitment.</p>
            <Link href="/booking" className="faq2-help-link">
              Book a free call <ArrowRight size={15} />
            </Link>
          </div>
        </aside>

        <ul className="faq2-list">
          {faqs.map((item, i) => (
            <li key={item.q} className="faq2-li">
              <details className="faq2-item" name={`faq-${uid}`} open={i === 0}>
                <summary className="faq2-q">
                  <span className="faq2-n">{String(i + 1).padStart(2, '0')}</span>
                  <span className="faq2-q-text">{item.q}</span>
                  <span className="faq2-icon" aria-hidden="true">
                    <Plus size={17} />
                  </span>
                </summary>
                <div className="faq2-a">
                  <p>{item.a}</p>
                </div>
              </details>
            </li>
          ))}
        </ul>
      </div>
    </section>
  )
}
